import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { ScrollView, Pressable } from "react-native";
import { PressableScale } from "pressto";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, FormInput, Switch, Card } from "@src/components";
import { UniversalDatePicker } from "@src/components/UniversalDatePicker";
import { TimePickerWith24Hour } from "@src/components/TimePickerWith24Hour";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { formatTime12 } from "@src/utils/format-date";
import { formatTime24 } from "@src/features/alarms/utils/format-time";
import { makeDateTimeRecord } from "@visioni/schedules";
import { isExceptionAllDayRange } from "@src/features/schedules/utils/schedule-utils";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { isWeb } from "@src/utils/web-helpers";
import type { AlarmAction, AlarmException } from "@src/services/api/types";
import { useAlarm, useUpdateAlarmExceptions } from "@src/services/api/hooks";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { universalConfirm } from "@src/components/UniversalAlert";
import { track } from "@visioni/a3s-analytics";
import { cameraScope } from "@src/features/cameras/analytics";
import { AlarmExceptionEdited, AlarmExceptionOpened } from "@src/features/alarms/analytics";
import { useTenantId } from "@src/features/tenants";
import Animated, {
  FadeOut,
  FadeIn,
  LinearTransition,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
  interpolateColor,
} from "react-native-reanimated";

export default function ExceptionFormScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const { theme } = useUnistyles();
  const tenantId = useTenantId();
  const { headerLeft } = useHeaderBackButton("/agent");

  const { siteId, cameraKey, exceptionId, ...params } = useLocalSearchParams<{
    siteId: string;
    cameraKey: string;
    exceptionId: string;
    reason?: string;
    action?: AlarmAction;
    startDate?: string;
    endDate?: string;
    startHour?: string;
    startMinute?: string;
    endHour?: string;
    endMinute?: string;
    endIs24Hour?: string;
  }>();

  const { data: alarm } = useAlarm(siteId, cameraKey);
  const updateExceptionsMutation = useUpdateAlarmExceptions();

  const isCreateMode = exceptionId === "new";
  const exception = useMemo(
    () => (!isCreateMode ? alarm?.settings?.exceptions.find((e) => e.id === exceptionId) : undefined),
    [isCreateMode, alarm?.settings?.exceptions, exceptionId],
  );

  // Once per exceptionId: refetches don't re-fire, but the web side panel can swap exceptions in place.
  const mode = isCreateMode ? "create" : "edit";
  const exceptionReady = isCreateMode || exception !== undefined;
  const openedExceptionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (exceptionReady && openedExceptionIdRef.current !== exceptionId) {
      openedExceptionIdRef.current = exceptionId;
      track(AlarmExceptionOpened, { ...cameraScope(tenantId, siteId, cameraKey), mode });
    }
  }, [exceptionReady, cameraKey, exceptionId, tenantId, siteId, mode]);

  const editedExceptionIdRef = useRef<string | null>(null);
  const markEdited = useCallback(() => {
    if (editedExceptionIdRef.current === exceptionId) return;
    editedExceptionIdRef.current = exceptionId;
    track(AlarmExceptionEdited, { ...cameraScope(tenantId, siteId, cameraKey), mode });
  }, [exceptionId, tenantId, siteId, cameraKey, mode]);

  const [reason, setReason] = useState("");
  const [action, setAction] = useState<AlarmAction>("armed");
  const [initialDateIso] = useState(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0)).toISOString();
  });
  const [startDate, setStartDate] = useState(initialDateIso);
  const [endDate, setEndDate] = useState(initialDateIso);
  const [startHour, setStartHour] = useState(0);
  const [startMinute, setStartMinute] = useState(0);
  const [endHour, setEndHour] = useState(0);
  const [endMinute, setEndMinute] = useState(0);
  const [endIs24Hour, setEndIs24Hour] = useState(false);
  const [isAllDay, setIsAllDay] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const isEndBeforeStart = useMemo(() => {
    const startDatePart = startDate.split("T")[0];
    const endDatePart = endDate.split("T")[0];

    if (isAllDay) return endDatePart < startDatePart;

    if (endDatePart < startDatePart) return true;
    if (endDatePart > startDatePart) return false;

    const finalEnd = endIs24Hour ? 24 : endHour;
    return finalEnd * 60 + endMinute < startHour * 60 + startMinute;
  }, [startDate, endDate, startHour, startMinute, endHour, endMinute, endIs24Hour, isAllDay]);

  // Extract specific values from exception to avoid object reference issues
  const exceptionReason = exception?.reason;
  const exceptionAction = exception?.action;
  const exceptionStartYear = exception?.period.start.year;
  const exceptionStartMonth = exception?.period.start.month;
  const exceptionStartDay = exception?.period.start.day;
  const exceptionStartHour = exception?.period.start.hour;
  const exceptionStartMinute = exception?.period.start.minute;
  const exceptionEndYear = exception?.period.end.year;
  const exceptionEndMonth = exception?.period.end.month;
  const exceptionEndDay = exception?.period.end.day;
  const exceptionEndHour = exception?.period.end.hour;
  const exceptionEndMinute = exception?.period.end.minute;

  const {
    reason: paramReason,
    action: paramAction,
    startDate: paramStartDate,
    endDate: paramEndDate,
    startHour: paramStartHour,
    startMinute: paramStartMinute,
    endHour: paramEndHour,
    endMinute: paramEndMinute,
    endIs24Hour: paramEndIs24Hour,
  } = params;

  // Animated active box color based on armed/disarmed state
  const actionProgress = useDerivedValue(() => withTiming(action === "armed" ? 0 : 1, { duration: 300 }));

  const activeBoxAnimatedStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      actionProgress.value,
      [0, 1],
      [
        theme.colors.alarmStateBadges.armed.background as string,
        theme.colors.alarmStateBadges.disarmed.background as string,
      ],
    ),
    borderColor: interpolateColor(
      actionProgress.value,
      [0, 1],
      [theme.colors.alarmStateBadges.armed.border as string, theme.colors.alarmStateBadges.disarmed.border as string],
    ),
  }));

  // Initialize form from exception (edit mode) or params (create mode with suggestion)
  useEffect(() => {
    if (exception) {
      setReason(exceptionReason ?? "");
      setAction(exceptionAction ?? "armed");

      if (exceptionStartYear !== undefined && exceptionStartMonth !== undefined && exceptionStartDay !== undefined) {
        const startDateObj = new Date(
          Date.UTC(exceptionStartYear, exceptionStartMonth - 1, exceptionStartDay, 12, 0, 0),
        );
        setStartDate(startDateObj.toISOString());
      }
      if (exceptionStartHour !== undefined) {
        setStartHour(exceptionStartHour === 24 ? 0 : exceptionStartHour);
      }
      if (exceptionStartMinute !== undefined) {
        setStartMinute(exceptionStartMinute);
      }

      if (exceptionEndYear !== undefined && exceptionEndMonth !== undefined && exceptionEndDay !== undefined) {
        const endDateObj = new Date(Date.UTC(exceptionEndYear, exceptionEndMonth - 1, exceptionEndDay, 12, 0, 0));
        setEndDate(endDateObj.toISOString());
      }
      if (exceptionEndHour !== undefined) {
        const is24Hour = exceptionEndHour === 24;
        setEndIs24Hour(is24Hour);
        setEndHour(is24Hour ? 0 : exceptionEndHour);
      }
      if (exceptionEndMinute !== undefined) {
        setEndMinute(exceptionEndMinute);
      }

      if (isExceptionAllDayRange(exception.period.start, exception.period.end)) {
        setIsAllDay(true);
      }
    } else if (isCreateMode) {
      if (paramReason) setReason(paramReason);
      if (paramAction) setAction(paramAction as AlarmAction);
      if (paramStartDate) {
        const datePart = paramStartDate.split("T")[0];
        const [year, month, day] = datePart.split("-").map(Number);
        const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        setStartDate(utcDate.toISOString());
      }
      if (paramEndDate) {
        const datePart = paramEndDate.split("T")[0];
        const [year, month, day] = datePart.split("-").map(Number);
        const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        setEndDate(utcDate.toISOString());
      }
      if (paramStartHour) setStartHour(parseInt(paramStartHour, 10));
      if (paramStartMinute) setStartMinute(parseInt(paramStartMinute, 10));
      if (paramEndHour) setEndHour(parseInt(paramEndHour, 10));
      if (paramEndMinute) setEndMinute(parseInt(paramEndMinute, 10));
      if (paramEndIs24Hour) setEndIs24Hour(paramEndIs24Hour === "true");
    }
  }, [
    exception,
    exceptionReason,
    exceptionAction,
    exceptionStartYear,
    exceptionStartMonth,
    exceptionStartDay,
    exceptionStartHour,
    exceptionStartMinute,
    exceptionEndYear,
    exceptionEndMonth,
    exceptionEndDay,
    exceptionEndHour,
    exceptionEndMinute,
    isCreateMode,
    paramReason,
    paramAction,
    paramStartDate,
    paramEndDate,
    paramStartHour,
    paramStartMinute,
    paramEndHour,
    paramEndMinute,
    paramEndIs24Hour,
  ]);

  const handleSave = useCallback(() => {
    if (!reason || !alarm || !cameraKey || isEndBeforeStart) return;

    const finalEndHour = endIs24Hour ? 24 : endHour;
    const startDatePart = startDate.split("T")[0];
    const [startYear, startMonth, startDay] = startDatePart.split("-").map(Number);
    const endDatePart = endDate.split("T")[0];
    const [endYear, endMonth, endDay] = endDatePart.split("-").map(Number);

    const timezone = getTimezoneFromAlarmDetail(alarm);
    if (!timezone) {
      showErrorToast({ message: "Error", description: "Schedule data is not available. Please try again." });
      return;
    }

    const period = {
      start: makeDateTimeRecord(startYear, startMonth, startDay, isAllDay ? 0 : startHour, isAllDay ? 0 : startMinute),
      end: makeDateTimeRecord(endYear, endMonth, endDay, isAllDay ? 24 : finalEndHour, isAllDay ? 0 : endMinute),
    };

    if (isCreateMode) {
      const newException: AlarmException = {
        id: `exception-${Date.now()}`,
        reason,
        action,
        author: null,
        period,
      };

      const updatedExceptions =
        alarm.settings?.exceptions ? [...alarm.settings.exceptions, newException] : [newException];

      updateExceptionsMutation.mutate(
        { siteId, cameraKey, exceptions: updatedExceptions, timezone, action: "created", exceptionAction: action },
        {
          onSuccess: () => {
            showSuccessToast({ message: "Exception created" });
            router.back();
          },
          onError: (error) => {
            console.error("Error creating exception:", error);
            showErrorToast({ message: "Error", description: "Failed to create exception. Please try again." });
          },
        },
      );
    } else {
      const updatedException: AlarmException = {
        id: exceptionId,
        reason,
        action,
        author: null,
        period,
      };

      const updatedExceptions =
        alarm.settings?.exceptions?.map((e) => (e.id === exceptionId ? updatedException : e)) ?? [];

      updateExceptionsMutation.mutate(
        { siteId, cameraKey, exceptions: updatedExceptions, timezone, action: "updated", exceptionAction: action },
        {
          onSuccess: () => {
            showSuccessToast({ message: "Exception saved" });
            router.back();
          },
          onError: (error) => {
            console.error("Error updating exception:", error);
            showErrorToast({ message: "Error", description: "Failed to update exception. Please try again." });
          },
        },
      );
    }
  }, [
    isCreateMode,
    cameraKey,
    siteId,
    alarm,
    exceptionId,
    reason,
    action,
    startDate,
    endDate,
    startHour,
    startMinute,
    endHour,
    endMinute,
    endIs24Hour,
    isAllDay,
    isEndBeforeStart,
    updateExceptionsMutation,
    router,
  ]);

  const handleRemove = useCallback(async () => {
    if (!alarm || !cameraKey) return;

    const confirmed = await universalConfirm({
      title: "Remove Exception",
      message: "Are you sure you want to remove this exception?",
      confirmLabel: "Remove",
    });
    if (!confirmed) return;

    const updatedExceptions = alarm.settings?.exceptions?.filter((e) => e.id !== exceptionId) ?? [];

    const timezone = getTimezoneFromAlarmDetail(alarm);
    if (!timezone) {
      showErrorToast({ message: "Error", description: "Schedule data is not available. Please try again." });
      return;
    }

    const removedException = alarm.settings?.exceptions?.find((e) => e.id === exceptionId);
    updateExceptionsMutation.mutate(
      {
        siteId,
        cameraKey,
        exceptions: updatedExceptions,
        timezone,
        action: "removed",
        exceptionAction: removedException?.action,
      },
      {
        onSuccess: () => {
          showSuccessToast({ message: "Exception deleted" });
          router.back();
        },
        onError: (error) => {
          console.error("Error deleting exception:", error);
          showErrorToast({ message: "Error", description: "Failed to delete exception. Please try again." });
        },
      },
    );
  }, [alarm, cameraKey, siteId, exceptionId, updateExceptionsMutation, router]);

  const formatTimeDisplay = (hour: number, minute: number, is24Hour = false): string => {
    if (is24Hour && hour === 0 && minute === 0) return "24:00";
    return is24Hour ? formatTime24(hour, minute) : formatTime12(hour, minute);
  };

  const saveDisabled = !reason || isEndBeforeStart || updateExceptionsMutation.isPending;

  const headerTitle = useMemo(
    () => (
      <View flexDirection="row" justifyContent="space-between" alignItems="center" flex={1}>
        <View flex={1}>
          <Text textStyle="md/semibold" color="text" textAlign="center" style={styles.headerTitle}>
            {reason || (isCreateMode ? "New Exception" : "Edit Exception")}
          </Text>
        </View>
        <PressableScale
          onPress={handleSave}
          style={[
            styles.doneButton,
            {
              opacity: saveDisabled ? 0.5 : 1,
            },
          ]}
          enabled={!saveDisabled}
        >
          <Text textStyle="md/medium" color={saveDisabled ? "textMuted" : "text"}>
            Done
          </Text>
        </PressableScale>
      </View>
    ),
    [handleSave, reason, isCreateMode, saveDisabled],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
      headerTitle: () => headerTitle,
    });
  }, [navigation, headerLeft, headerTitle]);

  if (!isCreateMode && !exception) {
    return (
      <View flex={1} bg="background" justifyContent="center" alignItems="center">
        <Text color="danger" textStyle="lg/regular">
          Exception not found
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.colors.background }}
      contentContainerStyle={{
        paddingBottom: theme.spacing["10xl"],
        paddingTop: theme.spacing["7xl"],
        paddingHorizontal: theme.spacing["5xl"],
        gap: theme.spacing["2xl"],
      }}
      keyboardDismissMode="on-drag"
    >
      {isWeb && (
        <View alignItems="flex-end" mb="xl">
          <Pressable onPress={handleSave} style={{ opacity: saveDisabled ? 0.5 : 1 }} disabled={saveDisabled}>
            <Text textStyle="md/semibold" color={saveDisabled ? "textMuted" : "navy"}>
              Done
            </Text>
          </Pressable>
        </View>
      )}

      {/* Reason */}
      <Card style={styles.card}>
        <View mb="lg">
          <Text textStyle="sm/regular" mb="lg">
            Name
          </Text>
          <FormInput
            value={reason}
            onChangeText={(text) => {
              markEdited();
              setReason(text);
            }}
            placeholder="Enter exception name"
            autoCapitalize="words"
            containerStyle={{
              backgroundColor: theme.colors.surfaceCard,
              borderColor: theme.colors.interactive.buttons.default_background,
            }}
          />
        </View>

        {/* Action */}
        <View mt="xl" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text textStyle="sm/regular">Action</Text>
          <SegmentedSelector
            optionsLabels={[
              { title: "Arm", icon: "lockClosed" },
              { title: "Disarm", icon: "lockOpen" },
            ]}
            selectedOption={action === "armed" ? "Arm" : "Disarm"}
            onSelectOption={(option) => {
              markEdited();
              setAction(option === "Arm" ? "armed" : "disarmed");
            }}
            width={isWeb ? 220 : 180}
            activeBoxStyle={activeBoxAnimatedStyle}
            containerStyle={styles.container}
          />
        </View>
      </Card>

      <Animated.View layout={LinearTransition} style={[styles.card, styles.animatedCard]}>
        {/* Start date/time */}
        <View mt="xl">
          <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="2xl">
            <Text textStyle="sm/regular">Starts</Text>

            <View flexDirection="row" alignItems="center" gap="md">
              <Animated.View layout={LinearTransition}>
                <UniversalDatePicker
                  value={startDate}
                  onChange={(date) => {
                    markEdited();
                    setStartDate(date);
                  }}
                  placeholder="Select start date"
                  size="medium"
                  textColor={theme.colors.text}
                  textInputPaddingLeft={0}
                  dateFormat="dd-MMM-yyyy"
                  containerFormInputStyle={{
                    paddingHorizontal: 0,
                    paddingLeft: theme.spacing.lg,
                    width: 120,
                    borderColor: theme.colors.interactive.buttons.default_background,
                  }}
                  showIcon={false}
                />
              </Animated.View>

              {!isAllDay && (
                <Animated.View entering={FadeIn} exiting={FadeOut}>
                  <PressableScale onPress={() => setShowStartTimePicker(true)}>
                    <View
                      bg="surface"
                      borderWidth={1}
                      py="md"
                      px="lg"
                      rounded={8}
                      flexDirection="row"
                      alignItems="center"
                      width={90}
                      justifyContent="center"
                      style={{ borderColor: theme.colors.interactive.buttons.default_background }}
                    >
                      <Text textStyle="md/regular" color="text" textAlign="center">
                        {formatTimeDisplay(startHour, startMinute)}
                      </Text>
                    </View>
                  </PressableScale>
                </Animated.View>
              )}
            </View>
          </View>
        </View>

        {/* End date/time */}
        <Animated.View layout={LinearTransition}>
          <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="2xl">
            <Text textStyle="sm/regular">Ends</Text>

            <View flexDirection="row" alignItems="center" gap="md">
              <Animated.View layout={LinearTransition}>
                <UniversalDatePicker
                  value={endDate}
                  onChange={(date) => {
                    markEdited();
                    setEndDate(date);
                  }}
                  placeholder="Select end date"
                  size="medium"
                  textColor={theme.colors.text}
                  textInputPaddingLeft={0}
                  dateFormat="dd-MMM-yyyy"
                  containerFormInputStyle={{
                    paddingHorizontal: 0,
                    paddingLeft: theme.spacing.lg,
                    width: 120,
                    borderColor: theme.colors.interactive.buttons.default_background,
                  }}
                  showIcon={false}
                />
              </Animated.View>

              {!isAllDay && (
                <Animated.View entering={FadeIn} exiting={FadeOut}>
                  <PressableScale onPress={() => setShowEndTimePicker(true)}>
                    <View
                      bg="surface"
                      borderWidth={1}
                      style={{ borderColor: theme.colors.interactive.buttons.default_background }}
                      py="md"
                      px="lg"
                      rounded={8}
                      flexDirection="row"
                      alignItems="center"
                      width={90}
                      justifyContent="center"
                    >
                      <Text textStyle="md/regular" color="text" textAlign="center">
                        {formatTimeDisplay(endHour, endMinute, endIs24Hour)}
                      </Text>
                    </View>
                  </PressableScale>
                </Animated.View>
              )}
            </View>
          </View>
        </Animated.View>

        {/* All day toggle */}
        <Animated.View layout={LinearTransition}>
          <View py="xl" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Text textStyle="sm/regular">All day</Text>
            <Switch
              active={isAllDay}
              onToggle={() => {
                markEdited();
                setIsAllDay(!isAllDay);
              }}
              size="large"
            />
          </View>
        </Animated.View>

        {isEndBeforeStart && (
          <Animated.View entering={FadeIn} exiting={FadeOut} layout={LinearTransition}>
            <Text textStyle="xs/regular" color="danger">
              End date must be after start date
            </Text>
          </Animated.View>
        )}

        {/* Time Pickers */}
        <TimePickerWith24Hour
          isVisible={showStartTimePicker}
          hour={startHour}
          minute={startMinute}
          onConfirm={(hour, minute) => {
            markEdited();
            setStartHour(hour);
            setStartMinute(minute);
            setShowStartTimePicker(false);
          }}
          onCancel={() => setShowStartTimePicker(false)}
        />
        <TimePickerWith24Hour
          isVisible={showEndTimePicker}
          hour={endHour}
          minute={endMinute}
          initialHour24={endIs24Hour}
          onConfirm={(hour, minute) => {
            markEdited();
            if (hour === 24) {
              setEndIs24Hour(true);
              setEndHour(0);
            } else {
              setEndIs24Hour(false);
              setEndHour(hour);
            }
            setEndMinute(minute);
            setShowEndTimePicker(false);
          }}
          onCancel={() => setShowEndTimePicker(false)}
        />
      </Animated.View>

      {!isCreateMode && (
        <Pressable
          onPress={handleRemove}
          disabled={updateExceptionsMutation.isPending}
          style={{
            opacity: updateExceptionsMutation.isPending ? 0.5 : 1,
            paddingVertical: theme.spacing.xl,
          }}
        >
          <Text textStyle="md/medium" color="danger" textAlign="center">
            Delete exception
          </Text>
        </Pressable>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create((theme) => ({
  doneButton: {
    paddingHorizontal: theme.spacing["5xl"],
    borderRadius: theme.spacing["6xl"],
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    marginRight: theme.spacing["5xl"],
    borderCurve: "continuous",
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: {
    marginLeft: theme.spacing["5xl"],
  },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderColor: theme.colors.borderCard,
  },
  container: {
    height: 40,
  },
  animatedCard: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    padding: theme.spacing["4xl"],
  },
}));
